import ChalkySticks from '@chalkysticks/sdk';
import * as Config from '@/Config';
import State from './State';

/**
 * Getters are used to calculate values based off state. They are generally
 * used for filtering or formatting data.
 *
 * @class Getters
 * @package Store/Module/Global
 * @project ChalkySticks App
 */
const getters = {
	/**
	 * Check if device is in landscape mode
	 *
	 * @param State state
	 * @return boolean
	 */
	isLandscape(state: typeof State): boolean {
		return window.innerWidth > window.innerHeight;
	},

	/**
	 * Get the current language code
	 *
	 * @param State state
	 * @return string
	 */
	languageCode(state: typeof State): string {
		return state.languageCode || 'en-US';
	},

	/**
	 * Get the pending match configuration, if any.
	 *
	 * @param State state
	 * @return Trainer.IMatchConfig | null
	 */
	matchConfig(state: typeof State): Trainer.IMatchConfig | null {
		return state.matchConfig;
	},

	/**
	 * Get the user model being navigated to
	 *
	 * @param State state
	 * @return ChalkySticks.Model.User | undefined
	 */
	navigatingUserModel(state: typeof State): ChalkySticks.Model.User | undefined {
		return state.navigatingUserModel;
	},

	/**
	 * Get the venue model being navigated to
	 *
	 * @param State state
	 * @return ChalkySticks.Model.Venue | undefined
	 */
	navigatingVenueModel(state: typeof State): ChalkySticks.Model.Venue | undefined {
		return state.navigatingVenueModel;
	},

	/**
	 * Query parameters from URL
	 *
	 * @param State state
	 * @return IQueryParameters
	 */
	queryParameters(state: typeof State): IQueryParameters {
		return state.queryParameters || {};
	},

	/**
	 * If we should show the splash. It resets every 24 hours so
	 * we don't have to see it on every refresh.
	 */
	shouldShowSplash(state: typeof State): boolean {
		const lastOpened = state.lastOpened;
		const threshold = Config.App.SPLASH_TIMEOUT;

		return Config.App.ENABLE_SPLASH && (!lastOpened || Date.now() - lastOpened > threshold);
	},

	/**
	 * Whether the primary context menu is visible
	 *
	 * @param State state
	 * @return boolean
	 */
	showPrimaryContextMenu(state: typeof State): boolean {
		return state.showPrimaryContextMenu || false;
	},
};

export default getters;
